import { getArrayRange, getDateOfBirthFromSAIDNumber } from '@ecdlink/core';
import {
  Alert,
  AlertProps,
  Button,
  ButtonGroup,
  ButtonGroupOption,
  ButtonGroupTypes,
  classNames,
  Divider,
  Dropdown,
  DropDownOption,
  FormInput,
  renderIcon,
  Typography,
} from '@ecdlink/ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { subYears } from 'date-fns';
import { useEffect, useState } from 'react';
import { useForm, useFormState, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { ShortMonths } from '../../../../constants/Dates';
import {
  ChildExtraInformationFormModel,
  childExtraInformationFormSchema,
} from '@schemas/child/child-registration/child-extra-information-form';
import {
  dobYearsBetweenHigher,
  dobYearsBetweenLower,
  dobYearsLess,
  idMismatchList,
  idMismatchMessage,
  invalidDateList,
  invalidDateMessage,
  olderList,
  olderMessage,
  yearsBetweenList,
  yearsHeading,
  yearsLessList,
  yearsMessage,
} from '@schemas/child/child-registration/child-information-form';
import { staticDataSelectors } from '@store/static-data';
import { calculateFullAge } from '@utils/common/date.utils';
import * as styles from './child-extra-information-form.styles';
import { ChildExtraInformationFormProps } from './child-extra-information-form.types';
import { ProgrammeAttendanceReasonDto } from '@ecdlink/core';

export const ChildExtraInformationForm: React.FC<
  ChildExtraInformationFormProps
> = ({ childExtraInformation, childName, onSubmit }) => {
  const daysInMonth = getArrayRange(1, 31);
  const years = getArrayRange(
    subYears(new Date(), 6).getFullYear(),
    new Date().getFullYear()
  );
  const [dayDropDownList, setDayDropDownList] = useState<
    DropDownOption<number>[]
  >([]);
  const [monthDropDownList] = useState<DropDownOption<number>[]>(ShortMonths);
  const [yearDropDownList, setYearDropDownList] = useState<
    DropDownOption<number>[]
  >([]);
  const [alerts, setAlerts] = useState<AlertProps[]>();

  const [racesList, setRacesList] = useState<DropDownOption<string>[]>([]);
  const [languagesList, setLanguagesList] = useState<
    ButtonGroupOption<string>[]
  >([]);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [childGender, setChildGender] = useState<string>();
  const [otherLanguageSelected, setOtherLanguageSelected] = useState<boolean>();
  const gender = useSelector(staticDataSelectors.getGenders);
  const languages = useSelector(staticDataSelectors.getLanguages);
  const races = useSelector(staticDataSelectors.getRaces);
  const [provideReason, setProvideReason] = useState(false);
  const reasons = useSelector(
    staticDataSelectors.getProgrammeAttendanceReasons
  );

  useEffect(() => {
    if (languages) {
      const languageListToAdd: ButtonGroupOption<string>[] = [];

      languages
        ?.filter((item) => item?.isActive === true)
        ?.forEach((language) => {
          languageListToAdd.push({
            text: language.description ?? '',
            value: language.id ?? '',
          });
        });
      setLanguagesList(languageListToAdd);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languages]);

  useEffect(() => {
    if (races) {
      const racesListToAdd: DropDownOption<string>[] = [];

      races.forEach((race) => {
        racesListToAdd.push({
          label: race.description ?? '',
          value: race.id ?? '',
        });
      });
      setRacesList(racesListToAdd);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [races]);

  useEffect(() => {
    if (childExtraInformation) {
      setChildGender(childExtraInformation.genderId);
      setSelectedLanguages(childExtraInformation.homeLanguages || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childExtraInformation]);

  const {
    getValues: getChildExtraInformationFormValues,
    setValue: setChildExtraInformationFormValue,
    register: childExtraInformationFormRegister,
    control: childExtraInformationFormControl,
    trigger: triggerChildExtraInformationForm,
  } = useForm<ChildExtraInformationFormModel>({
    resolver: yupResolver(childExtraInformationFormSchema),
    mode: 'onChange',
    defaultValues: childExtraInformation,
  });

  const {
    childIdField,
    dobDay,
    dobMonth,
    dobYear,
    dobValid,
    reason,
    otherReason,
  } = useWatch({
    control: childExtraInformationFormControl,
    defaultValue: childExtraInformation,
  });

  const { isValid, errors } = useFormState({
    control: childExtraInformationFormControl,
  });

  useEffect(() => {
    const dayDropDownListToAdd: DropDownOption<number>[] = [];
    const yearDropDownListToAdd: DropDownOption<number>[] = [];

    daysInMonth.forEach((day) => {
      dayDropDownListToAdd.push({ label: day.toString(), value: day });
    });

    setDayDropDownList(dayDropDownListToAdd);
    years.forEach((year) => {
      yearDropDownListToAdd.push({ label: year.toString(), value: year });
    });

    setYearDropDownList(yearDropDownListToAdd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDateOfBirthById();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childIdField]);

  useEffect(() => {
    if (dobDay && dobMonth && dobYear) {
      const dateOfBirth = new Date(dobYear, dobMonth - 1, dobDay);
      setChildExtraInformationFormValue('dob', dateOfBirth);
      validateDateOfBirth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dobDay, dobMonth, dobYear]);

  const validateDateOfBirth = () => {
    const alertsArray: AlertProps[] = [];

    if (!dobDay || !dobMonth || !dobYear) {
      setChildExtraInformationFormValue('dobValid', false);
      return;
    }

    const idDateOfBirth = getDateOfBirthFromSAIDNumber(childIdField);
    if (idDateOfBirth) {
      const hasMisMatchWithId =
        dobDay !== idDateOfBirth.getDate() ||
        dobMonth !== idDateOfBirth.getMonth() + 1 ||
        dobYear !== idDateOfBirth.getFullYear();

      if (hasMisMatchWithId) {
        alertsArray.push({
          type: 'warning',
          title: idMismatchMessage,
          list: idMismatchList,
        });
      }
    }

    // js months start at 0
    const dateOfBirth = new Date(dobYear, dobMonth - 1, dobDay);
    const today = new Date();
    const { years: childAge } = calculateFullAge(dateOfBirth);

    let yearHigherThresholdBreach = false;

    // calculateFullAge measures an absolute interval, so a date in the future comes back as a
    // large positive age. Check for a future date first, otherwise it is reported as a child
    // older than 10 rather than as an invalid date.
    const isFutureDate = dateOfBirth >= today;

    if (
      !isFutureDate &&
      childAge > dobYearsBetweenLower &&
      childAge < dobYearsBetweenHigher
    ) {
      alertsArray.push({
        type: 'warning',
        title: yearsHeading(childAge),
        message: yearsMessage,
        list: yearsBetweenList,
      });
    }

    if (isFutureDate) {
      alertsArray.push({
        type: 'error',
        title: invalidDateMessage,
        list: invalidDateList,
      });
    } else if (childAge >= dobYearsBetweenHigher) {
      alertsArray.push({
        type: 'error',
        title: olderMessage,
        list: olderList,
      });
      yearHigherThresholdBreach = true;
    } else if (childAge < dobYearsLess) {
      alertsArray.push({
        type: 'warning',
        title: yearsHeading(childAge),
        message: yearsMessage,
        list: yearsLessList,
      });
    }

    if (!yearHigherThresholdBreach && alertsArray.length === 0) {
      alertsArray.push({
        type: 'info',
        title: `${yearsHeading(childAge)}`,
      });
    }

    if (alertsArray.filter((x) => x.type === 'error').length > 0) {
      setChildExtraInformationFormValue('dobValid', false);
    } else {
      setChildExtraInformationFormValue('dobValid', true);
    }
    triggerChildExtraInformationForm();
    setProvideReason(!isFutureDate && childAge > dobYearsBetweenLower);
    setAlerts(alertsArray);
  };

  const setDateOfBirthById = () => {
    // Only fill the date of birth once the ID is a complete, valid SA ID. This used to run on any
    // six characters and hardcode the century as 20, so a half-typed ID - or one beginning 90 -
    // silently populated the field with a nonsense or future date, which the age check then
    // reported as "child is older than 10".
    const idDateOfBirth = getDateOfBirthFromSAIDNumber(childIdField);
    if (!idDateOfBirth) return;

    setChildExtraInformationFormValue('dobYear', idDateOfBirth.getFullYear());
    setChildExtraInformationFormValue('dobMonth', idDateOfBirth.getMonth() + 1);
    setChildExtraInformationFormValue('dobDay', idDateOfBirth.getDate());
  };

  const handleFormSubmit = () => {
    if (isValid && onSubmit) {
      onSubmit(getChildExtraInformationFormValues());
    }
  };

  const handleLanguageSelection = (selectedLanguages: string[]) => {
    setSelectedLanguages(selectedLanguages);
    setChildExtraInformationFormValue('homeLanguages', selectedLanguages);

    const languageOther = languagesList.find((x) => x.text === 'Other');

    if (selectedLanguages.indexOf(languageOther?.value || '-1') !== -1) {
      setOtherLanguageSelected(true);
    } else {
      setOtherLanguageSelected(false);
    }
    triggerChildExtraInformationForm();
  };

  return (
    <div className={'h-full bg-white px-4 pt-2 pb-4'}>
      <Typography type={'h1'} text={childName} color={'primary'} />
      <Typography type={'h2'} text={'Extra Information'} color={'textMid'} />
      <div>
        <Dropdown
          placeholder={'Select race'}
          list={racesList}
          fillType="clear"
          label={'Race'}
          fullWidth
          className={'mt-3 w-full'}
          selectedValue={getChildExtraInformationFormValues().race}
          onChange={(item: string) => {
            setChildExtraInformationFormValue('race', item);
            triggerChildExtraInformationForm();
          }}
        />

        <label className={classNames(styles.label, 'mt-4')}>{'Sex'}</label>
        <div className={'mt-2'}>
          <ButtonGroup<string>
            options={
              (gender &&
                gender
                  ?.filter((item) => item?.isActive === true)
                  ?.map((x) => {
                    return { text: x.description, value: x.id ?? '' };
                  })) ||
              []
            }
            onOptionSelected={(value: string | string[]) => {
              setChildExtraInformationFormValue('genderId', value as string);
              setChildGender(value as string);
              triggerChildExtraInformationForm();
            }}
            selectedOptions={childGender}
            color="secondary"
            type={ButtonGroupTypes.Button}
            className={'w-full'}
            multiple={false}
          />
        </div>
        <label className={classNames(styles.label, 'mt-4')}>
          {`${childName}’s home language(s)?`}
        </label>
        <label className="text-textLight mt-2 text-sm">
          You can choose more than one
        </label>
        <div className={'mt-2'}>
          <ButtonGroup<string>
            type={ButtonGroupTypes.Chip}
            options={languagesList}
            onOptionSelected={(value: string | string[]) =>
              handleLanguageSelection(value as string[])
            }
            multiple
            selectedOptions={selectedLanguages}
            color="secondary"
          />
        </div>
        {otherLanguageSelected && (
          <FormInput<ChildExtraInformationFormModel>
            label={'Please type in other language(s)'}
            className={'mt-3'}
            register={childExtraInformationFormRegister}
            nameProp={'otherLanguages'}
            placeholder={'E.g. Portuguese'}
          />
        )}
        <FormInput<ChildExtraInformationFormModel>
          label={'ID number'}
          maxLength={13}
          className={'mt-4'}
          hint={'Leave blank if not available'}
          nameProp={'childIdField'}
          register={childExtraInformationFormRegister}
          error={errors['childIdField']}
          placeholder={'E.g. 190101 0000 000'}
        />
        <label className={classNames(styles.label, 'mt-4')}>
          {'Date of birth'}
        </label>
        <div className={'flex items-center justify-between'}>
          <Dropdown
            placeholder={'Day'}
            fillType="clear"
            selectedValue={getChildExtraInformationFormValues().dobDay}
            list={dayDropDownList}
            onChange={(item) => {
              setChildExtraInformationFormValue('dobDay', item as number);
            }}
          />
          <Dropdown
            placeholder={'Month'}
            fillType="clear"
            list={monthDropDownList}
            selectedValue={getChildExtraInformationFormValues().dobMonth}
            onChange={(item) => {
              setChildExtraInformationFormValue('dobMonth', item as number);
            }}
          />
          <Dropdown
            placeholder={'Year'}
            fillType="clear"
            list={yearDropDownList}
            selectedValue={getChildExtraInformationFormValues().dobYear}
            onChange={(item) => {
              setChildExtraInformationFormValue('dobYear', item as number);
            }}
          />
        </div>

        {provideReason && (
          <>
            <label className={classNames(styles.label, 'mt-4')}>
              {'Reason for child registration'}
            </label>
            <Dropdown<ProgrammeAttendanceReasonDto>
              placeholder={!!reason ? reason?.reason : 'Select reason'}
              fullWidth
              fillType="clear"
              list={
                (reasons &&
                  reasons.map((x: ProgrammeAttendanceReasonDto) => {
                    return { label: x.reason, value: x };
                  })) ||
                []
              }
              selectedValue={getChildExtraInformationFormValues().reason}
              onChange={(item) => {
                setChildExtraInformationFormValue('reason', item);
              }}
            />
          </>
        )}

        {reason?.reason === 'Other' && provideReason && (
          <FormInput<ChildExtraInformationFormModel>
            label={'Reason'}
            className={'mt-3'}
            textInputType="textarea"
            register={childExtraInformationFormRegister}
            nameProp={'otherReason'}
            placeholder={'Please enter reason'}
          />
        )}

        {alerts &&
          alerts.map((alert: AlertProps, index: number) => {
            return (
              <Alert
                key={'child-reg-alert-' + index}
                className={'mt-5'}
                title={alert.title}
                message={alert.message}
                list={alert.list}
                type={alert.type}
              />
            );
          })}
        <div className={'py-4'}>
          <Divider></Divider>
        </div>
        <Button
          onClick={handleFormSubmit}
          className="w-full"
          size="small"
          color="primary"
          type="filled"
          disabled={
            !isValid ||
            !dobDay ||
            !dobMonth ||
            !dobYear ||
            (provideReason &&
              (!reason ||
                (!!reason && reason?.reason === 'Other' && !otherReason)))
          }
        >
          {renderIcon('ArrowCircleRightIcon', 'h-5 w-5 text-white')}
          <Typography type="h6" className="ml-2" text="Next" color="white" />
        </Button>
      </div>
    </div>
  );
};
